/** The `access` area: an item's access dials, the shared Access panel, the
 *  request for access, and a view's audience. Owned by the stage 2 slot
 *  components-b (docs/I18N.md §11). */
export const access = {
  // --- An item's two access dials (access-controls.tsx) -----------------------
  /** `label`: the dial's name. */
  'access.dial.options': '{label} options',
  'access.dial.confirm': 'Yes, apply it',
  'access.visibility.label': 'Visibility',
  'access.visibility.private': 'Private',
  'access.visibility.open': 'Open to everyone',
  'access.visibility.privateWhat':
    'Only people and groups with a path to this item can see it (the org wall always holds).',
  'access.visibility.openWhat':
    'Everyone in this space can see this item: in the commons, that is everyone on Tesserano. Access stays logged.',
  'access.visibility.confirm': 'Yes, open it up',
  'access.inherit.label': 'Access',
  'access.inherit.inheritsState': 'inherits from parents',
  'access.inherit.brokenState': 'chain broken',
  'access.inherit.inherits': 'Inherits from parents',
  'access.inherit.inheritsWhat': 'People with access to a parent item can see this one too.',
  'access.inherit.confirm': 'Yes, reconnect it',
  'access.inherit.broken': 'Chain broken',
  'access.inherit.brokenWhat':
    'This item stops receiving access from its parents; from here down only direct grants count.',

  // --- The shared Access panel (access-panel.tsx) -----------------------------
  'access.panel.addAs': 'Add as',
  'access.panel.roleLabel': 'Role to grant the batch',
  'access.panel.cancel': 'Cancel',
  'access.panel.apply': 'Apply',
  'access.panel.placeholder': 'Add people and groups…',
  'access.panel.empty': 'No one left to add.',
  'access.panel.remove': 'Remove {name}',
  'access.panel.nobody': 'No one yet.',

  // --- Asking for access (request-access.tsx) ---------------------------------
  'access.request.already': 'Already requested.',
  'access.request.done': 'Access requested. An owner will see it on the log.',
  /** `role`: the role picker, inside the sentence. */
  'access.request.line': 'Request {role} access',
  'access.request.roleLabel': 'Role to request',
  'access.request.submit': 'Request',
  /** The stored role values, as the picker shows them. */
  'access.request.role.viewer': 'viewer',
  'access.request.role.member': 'member',
  'access.request.role.owner': 'owner',

  // --- A view's audience (view-audience.tsx) ----------------------------------
  'access.role.viewer': 'Viewer',
  'access.role.member': 'Member',
  'access.role.owner': 'Owner',
  'access.audience.viewerHint': 'sees it',
  'access.audience.memberHint': 'works through it',
  'access.audience.ownerHint': 'governs it',
  'access.audience.roleFor': 'Role for {name}',
  'access.audience.title': 'Audience',
  'access.audience.titleBoard': 'Audience of this board',
  'access.audience.titleView': 'Audience of this view',
  'access.audience.sentenceBoard':
    'Everyone here sees the items on this board. Members may add work. Owners configure it.',
  'access.audience.sentenceView':
    'Everyone here can open this view. It shows each person only what they can see anyway. Owners can change its saved setup.',
  'access.audience.boardViewer': 'Sees the items',
  'access.audience.boardMember': 'May add work and act on it',
  'access.audience.boardOwner': 'Configures the board',
  'access.audience.viewViewer': 'Opens it',
  'access.audience.viewOwner': 'Maintains it',
  'access.audience.loading': 'Loading…',
  'access.audience.unreachable': 'Not reachable.',
  'access.audience.footnote': 'Audience changes are recorded in its activity.',
  'access.mint.defaultName': '{name} (shared)',
  'access.mint.saveTitle': 'Save as board',
  'access.mint.shareTitle': 'Share as a new board',
  'access.mint.saveSubtitle':
    'A new board remembering this filter and sort; “{source}” stays as it is.',
  'access.mint.shareSubtitle': 'A new board seeded from “{source}”; the source stays as it is.',
  'access.mint.cancel': 'Cancel',
  'access.mint.save': 'Save board',
  'access.mint.create': 'Create board',
  'access.mint.name': 'Name',
  'access.mint.nameLabel': 'New board name',
  'access.mint.audienceHint':
    'Who the new board reaches. Its cards are the work granted to this audience - items only you can see stay on the source.',
  'access.share.title': 'Share the work',
  'access.share.subtitle': 'Grant the items on “{view}” to people or groups.',
  'access.share.counting': 'Counting the items on this board…',
  'access.share.none': 'This board holds no items for you - nothing to share.',
  'access.share.count': {
    one: '{count} item on this board. Each is shared through your own authority - anything you may not share is skipped and listed here, never granted.',
    other:
      '{count} items on this board. Each is shared through your own authority - anything you may not share is skipped and listed here, never granted.',
  },
  'access.share.placeholder': 'Share with people and groups…',
  'access.share.viewerHint': 'sees the work',
  'access.share.memberHint': 'works on it',
  'access.share.ownerHint': 'co-owns it',
  /** The result line's facts, side by side. */
  'access.share.shared': 'Shared {count}',
  'access.share.already': '{count} already shared',
  'access.share.skipped': '{count} skipped',
  'access.share.unnamed': 'An item you can see but not share',
  'access.share.lacks': 'you lack the authority to share it',
  'access.share.otherOrg': 'a group you chose belongs to a different org than this item',
} as const;
